import { isAcTerminalId } from "./ac";
import { analyzeBatterySourceRanges } from "./battery-stack";
import type { SimulateContext } from "./cable-current";
import { getDefinition, type DefLookup } from "./catalog";
import { dcPortTerminalGroups } from "./component-ports";
import { dcOutputPorts } from "./dc-output-ports";
import { effectiveTerminals } from "./effective";
import {
  buildGraph, reachable, splitV, v,
  type Edge, type Graph, type Vertex,
} from "./diagnostics/graph";
import type { System } from "./types";

/** A declared electrical port. Independent converter ports keep separate IDs. */
export type DcPort = {
  id: string;
  nodeId: string;
  positive: Vertex[];
  negative: Vertex[];
};

export type DcSourcePair = {
  id: string;
  kind: "battery" | "converter-output";
  nodeId?: string;
  positive: Vertex[];
  negative: Vertex[];
  batteryNodeIds: string[];
  levels: string[][];
  nominalV?: number;
};

/** Conventional discharge direction, relative to the cable's stored endpoints. */
export type DcCableStep = {
  cableId: string;
  from: Vertex;
  to: Vertex;
  direction: 1 | -1;
};

/** A voltage-producing device transition, never a conductive graph edge. */
export type DcSourceTransition = {
  nodeId: string;
  negative: Vertex;
  positive: Vertex;
};

export type DcCircuitRoute = {
  sourceId: string;
  source: DcSourcePair;
  consumerPortId: string;
  consumerNodeId: string;
  positiveTerminal: Vertex;
  negativeTerminal: Vertex;
  /** Source + to consumer +. */
  positive: DcCableStep[];
  /** Consumer - to the SAME source's -. */
  negative: DcCableStep[];
  /** Lower-cell + to upper-cell -, in source order. */
  series: DcCableStep[];
  sourceTransitions: DcSourceTransition[];
  /** Full vertex paths retain post boundaries without imposing fuse policy. */
  positiveVertices: Vertex[];
  negativeVertices: Vertex[];
  complete: true;
};

export type DcIncompleteRoute = {
  sourceId: string;
  source: DcSourcePair;
  consumerPortId: string;
  positiveTerminal: Vertex;
  positive: DcCableStep[];
  positiveVertices: Vertex[];
  reason: "missing-return" | "unpaired-port" | "shorted-rails";
  complete: false;
};

export type DcCircuitResolution = {
  routes: DcCircuitRoute[];
  incomplete: DcIncompleteRoute[];
  /** No partial route set is returned for a source whose expansion is bounded. */
  unsupported: Array<{ sourceId: string; reason: "source-route-limit" | "source-range-limit" }>;
};

export type DcCircuitTopology = {
  /** Physical DC conductors only: no battery/converter bridges or fuse cuts.
   * Routes are deterministic witnesses; this graph retains parallel copper
   * alternatives for a current router rather than assuming one path carries it. */
  graph: Graph;
  sources: DcSourcePair[];
  batteryPosts: ReadonlySet<Vertex>;
  /** Device ports for evidence/provenance. Never conductive graph edges. */
  batteryPorts: ReadonlyMap<string, { positive: Vertex[]; negative: Vertex[] }>;
  invalidCableIds: ReadonlySet<string>;
  unsupported: ReadonlyArray<{ reason: "source-range-limit" }>;
  railId(vertex: Vertex): string;
  resolvePort(port: DcPort): DcCircuitResolution;
};

type Walk = Map<Vertex, Vertex | null>;
type SourceBranch = { series: DcCableStep[]; sourceTransitions: DcSourceTransition[] };
// This is a computational limit, not an electrical assumption. A bounded
// refusal stays explicit so a caller cannot mistake truncated paths for zero.
const MAX_SOURCE_ROUTES = 256;
const cache = new WeakMap<System, WeakMap<DefLookup, Map<SimulateContext | undefined, DcCircuitTopology>>>();

/** Physical completeness is separate from live power availability. In
 * particular, a declared converter output is a design source pair; this does
 * not assert that the converter has a live input or is producing power. */
export function buildDcCircuitTopology(
  system: System,
  lookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
): DcCircuitTopology {
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  let byState = byLookup.get(lookup);
  if (!byState) { byState = new Map(); byLookup.set(lookup, byState); }
  const known = byState.get(simulate);
  if (known) return known;
  const topology = buildTopology(system, lookup, simulate);
  byState.set(simulate, topology);
  return topology;
}

/** Demand-port selection is shared by ordinary loads and engine starters.
 * Alternator outputs and independent converter/PV inputs are never merged. */
export function dcInputPorts(system: System, lookup: DefLookup = getDefinition): DcPort[] {
  return system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def || !["load", "charger", "engine", "monitor"].includes(def.category)) return [];
    return dcPortTerminalGroups(def, "input").map((terms, i) => ({
      id: `${node.id}:input:${i}`,
      nodeId: node.id,
      positive: terms.filter(t => t.polarity === "positive").map(t => v(node.id, t.id)),
      negative: terms.filter(t => t.polarity === "negative" || t.polarity === "ground").map(t => v(node.id, t.id)),
    }));
  });
}

export function resolveDcPortCircuits(topology: DcCircuitTopology, port: DcPort): DcCircuitResolution {
  return topology.resolvePort(port);
}

function buildTopology(system: System, lookup: DefLookup, simulate?: SimulateContext): DcCircuitTopology {
  const physical = buildGraph(system, lookup, { simulate, excludeSense: true });
  const valid = new Set<Vertex>();
  const batteryPosts = new Set<Vertex>();
  const batteryTerminals = new Map<string, { positive: Vertex[]; negative: Vertex[] }>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminals = effectiveTerminals(def, node).filter(t => !isAcTerminalId(t.id));
    for (const terminal of terminals) valid.add(v(node.id, terminal.id));
    if (def.category !== "battery") continue;
    const positive = terminals.filter(t => t.polarity === "positive").map(t => v(node.id, t.id));
    const hasNegative = terminals.some(t => t.polarity === "negative");
    const negative = terminals.filter(t => t.polarity === "negative" || (!hasNegative && t.polarity === "ground")).map(t => v(node.id, t.id));
    batteryTerminals.set(node.id, { positive, negative });
    for (const terminal of [...positive, ...negative]) batteryPosts.add(terminal);
  }
  const invalidCableIds = new Set(system.cables.filter(c =>
    !valid.has(v(c.fromNodeId, c.fromTerminalId)) || !valid.has(v(c.toNodeId, c.toTerminalId)),
  ).map(c => c.id));
  const graph: Graph = {
    ...physical,
    adj: new Map([...physical.adj].filter(([vertex]) => valid.has(vertex)).map(([vertex, edges]) =>
      [vertex, edges.filter(edge => valid.has(edge.vertex))],
    )),
  };
  const regions = new Map<Vertex, string>();
  const railId = (vertex: Vertex): string => {
    const known = regions.get(vertex);
    if (known != null) return known;
    const members = [...reachable(graph, vertex)].sort();
    const id = members[0] ?? vertex;
    for (const member of members) regions.set(member, id);
    return id;
  };
  const sourceAnalysis = analyzeBatterySourceRanges(system, lookup, graph, simulate);
  const unsupported: DcCircuitTopology["unsupported"] = sourceAnalysis.complete ? [] : [{ reason: sourceAnalysis.reason }];
  const sources: DcSourcePair[] = sourceAnalysis.ranges.map(range => ({
    id: `battery:${range.levels.map(level => level.map(id => encodeURIComponent(id)).sort().join(",")).join(">")}`,
    kind: "battery",
    positive: [...range.topPositives].sort(),
    negative: [...range.bottomNegatives].sort(),
    batteryNodeIds: [...range.batteryNodeIds].sort(),
    levels: range.levels.map(level => [...level].sort()),
    nominalV: range.nominalV,
  }));
  for (const output of dcOutputPorts(system, lookup)) sources.push({
    id: `output:${output.nodeId}:${[...output.positive, ...output.negative].join(",")}`,
    kind: "converter-output", nodeId: output.nodeId,
    positive: output.positive, negative: output.negative,
    batteryNodeIds: [], levels: [],
  });
  sources.sort((a, b) => a.id.localeCompare(b.id));
  const walks = new Map<Vertex, Walk>();
  const walk = (seed: Vertex): Walk => {
    let prev = walks.get(seed);
    if (prev) return prev;
    prev = new Map([[seed, null]]);
    const queue = [seed];
    for (let i = 0; i < queue.length; i++) {
      const edges = [...(graph.adj.get(queue[i]) ?? [])].sort((a, b) =>
        a.vertex.localeCompare(b.vertex) || edgeId(a.via).localeCompare(edgeId(b.via)));
      for (const { vertex } of edges) {
        if (prev.has(vertex)) continue;
        prev.set(vertex, queue[i]); queue.push(vertex);
      }
    }
    walks.set(seed, prev);
    return prev;
  };
  const cables = new Map(system.cables.map(c => [c.id, c]));
  const connection = (start: Vertex, end: Vertex): { steps: DcCableStep[]; vertices: Vertex[] } | undefined => {
    const prev = walk(start);
    if (!prev.has(end)) return undefined;
    const vertices = [end];
    for (let vertex = end; prev.get(vertex) != null;) {
      vertex = prev.get(vertex)!; vertices.push(vertex);
    }
    vertices.reverse();
    const steps: DcCableStep[] = [];
    for (let i = 1; i < vertices.length; i++) {
      const from = vertices[i - 1], to = vertices[i];
      const edges = graph.adj.get(from)?.filter(edge => edge.vertex === to) ?? [];
      const via = edges.sort((a, b) => edgeId(a.via).localeCompare(edgeId(b.via)))[0]?.via;
      if (via?.kind !== "cable") continue;
      const cable = cables.get(via.cableId)!;
      steps.push({ cableId: cable.id, from, to, direction: v(cable.fromNodeId, cable.fromTerminalId) === from ? 1 : -1 });
    }
    return { steps, vertices };
  };
  const branchCache = new Map<string, SourceBranch[] | null>();
  const sourceBranches = (source: DcSourcePair, positive: Vertex, negative: Vertex): SourceBranch[] | null => {
    if (source.kind !== "battery") return [{ series: [], sourceTransitions: [] }];
    const key = JSON.stringify([source.id, positive, negative]);
    if (branchCache.has(key)) return branchCache.get(key)!;
    // Bound depth as well as breadth before recursive expansion.
    if (source.levels.length > MAX_SOURCE_ROUTES) { branchCache.set(key, null); return null; }
    const lowerId = splitV(negative).nodeId;
    const memo = new Map<string, SourceBranch[] | null>();
    const visit = (level: number, output: Vertex): SourceBranch[] | null => {
      const subkey = JSON.stringify([level, output]);
      if (memo.has(subkey)) return memo.get(subkey)!;
      const nodeId = splitV(output).nodeId;
      if (level === 0) return nodeId === lowerId
        ? [{ series: [], sourceTransitions: [{ nodeId, negative, positive: output }] }] : [];
      const paths: SourceBranch[] = [];
      for (const input of batteryTerminals.get(nodeId)?.negative ?? []) {
        for (const lowerNode of [...source.levels[level - 1]].sort()) {
          for (const lowerPositive of batteryTerminals.get(lowerNode)?.positive ?? []) {
            const jumper = connection(lowerPositive, input);
            if (!jumper) continue;
            const earlierPaths = visit(level - 1, lowerPositive);
            if (earlierPaths === null || paths.length + earlierPaths.length > MAX_SOURCE_ROUTES) {
              memo.set(subkey, null); return null;
            }
            for (const earlier of earlierPaths) paths.push({
              series: [...earlier.series, ...jumper.steps],
              sourceTransitions: [...earlier.sourceTransitions, { nodeId, negative: input, positive: output }],
            });
          }
        }
      }
      memo.set(subkey, paths);
      return paths;
    };
    const result = visit(source.levels.length - 1, positive);
    branchCache.set(key, result);
    return result;
  };
  const batteryPositiveRails = new Set([...batteryTerminals.values()].flatMap(terminals => terminals.positive.map(railId)));
  const resolvePort = (port: DcPort): DcCircuitResolution => {
    const routes: DcCircuitRoute[] = [], incomplete: DcIncompleteRoute[] = [];
    // A bounded battery graph must not make an unrelated isolated converter
    // output look unresolved. Flag only ports on a modeled battery + rail.
    const unsupported: DcCircuitResolution["unsupported"] = sourceAnalysis.complete ||
      !port.positive.some(vertex => batteryPositiveRails.has(railId(vertex))) ? [] : [{
        sourceId: "battery-sources", reason: sourceAnalysis.reason,
      }];
    for (const source of sources) {
      if (source.nodeId === port.nodeId) continue;
      const routeStart = routes.length, incompleteStart = incomplete.length;
      let sourceLimit = false;
      sourceRoutes: for (const sourcePositive of [...source.positive].sort()) {
        if (!port.positive.every(vertex => walk(sourcePositive).has(vertex))) continue;
        for (const positiveTerminal of port.positive) {
          const positive = connection(sourcePositive, positiveTerminal);
          if (!positive) continue;
          let completed = false;
          let shorted = false;
          for (const sourceNegative of source.negative) {
            if (railId(sourcePositive) === railId(sourceNegative)) { shorted = true; continue; }
            if (!port.negative.length || !port.negative.every(vertex => walk(sourceNegative).has(vertex))) continue;
            for (const negativeTerminal of port.negative) {
              const negative = connection(negativeTerminal, sourceNegative);
              if (!negative) continue;
              const branches = sourceBranches(source, sourcePositive, sourceNegative);
              if (branches === null || routes.length - routeStart + branches.length > MAX_SOURCE_ROUTES) {
                sourceLimit = true; break sourceRoutes;
              }
              for (const branch of branches) {
                routes.push({
                  sourceId: source.id, source, consumerPortId: port.id, consumerNodeId: port.nodeId,
                  positiveTerminal, negativeTerminal,
                  positive: positive.steps, negative: negative.steps, ...branch,
                  positiveVertices: positive.vertices, negativeVertices: negative.vertices,
                  complete: true,
                });
                completed = true;
              }
            }
          }
          if (!completed) incomplete.push({
            sourceId: source.id, source, consumerPortId: port.id, positiveTerminal,
            positive: positive.steps, positiveVertices: positive.vertices,
            reason: shorted ? "shorted-rails" : port.negative.length ? "missing-return" : "unpaired-port",
            complete: false,
          });
        }
      }
      if (sourceLimit) {
        // Refuse the whole source, including earlier witnesses. A partial set
        // would silently understate some conductors or source alternatives.
        routes.splice(routeStart); incomplete.splice(incompleteStart);
        unsupported.push({ sourceId: source.id, reason: "source-route-limit" });
      }
    }
    return { routes, incomplete, unsupported };
  };
  return { graph, sources, batteryPosts, batteryPorts: batteryTerminals, invalidCableIds, unsupported, railId, resolvePort };
}

function edgeId(edge: Edge): string {
  return edge.kind === "cable" ? `c:${edge.cableId}` : `i:${edge.link.nodeId}:${edge.link.from}:${edge.link.to}`;
}
